// dwaar/src/pages/Srishti.jsx
// Repo: terralekha-core | Folder: dwaar/src/pages/
// Srishti — Sanskrit: सृष्टि — creation, manifestation.
//   New PassPort creation page. Collects CN code + reporting period,
//   inserts a draft PassPort into Supabase, and redirects to /dashboard.
//
// Data flow:
//   1. On mount: getSession → userId; fetch users(gstin) + organisations(sector)
//   2. User selects CN code (sector-filtered, searchable) + period dates
//   3. On submit: insert into public.passports (draft status)
//   4. On success: navigate to /dashboard
//
// Reveal doctrine:
//   Supabase errors stay in console. User sees only PUBLIC_ERROR.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSupabaseClient } from "../lib/supabaseClient.js";
import { searchCodes } from "../lib/suchi.js";
import { PASSPORT_LABEL, PUBLIC_ERROR } from "../lib/brand.js";
import Koshtha from "../components/Koshtha.jsx";

// ── Phase ─────────────────────────────────────────────────────────────────────

const PHASE = Object.freeze({
  LOADING:    "loading",
  READY:      "ready",
  SUBMITTING: "submitting",
  ERROR:      "error",
});

// ── CN Code Combobox ──────────────────────────────────────────────────────────
// Accessible combobox: input + listbox. Keyboard-navigable.
// aria-label on input: "CN code" — queryable by screen readers and RTL tests.

function CnCodeCombobox({ sector, value, onChange }) {
  const [query,    setQuery]    = useState("");
  const [open,     setOpen]     = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef   = useRef(null);
  const listRef    = useRef(null);

  const results = searchCodes(sector, query);

  function selectOption(entry) {
    onChange(entry);
    setQuery("");
    setOpen(false);
    setActiveIdx(-1);
  }

  function handleInputChange(e) {
    setQuery(e.target.value);
    setOpen(true);
    setActiveIdx(-1);
    if (!e.target.value && value) onChange(null); // clear on empty
  }

  function handleKeyDown(e) {
    if (!open) { if (e.key === "ArrowDown") setOpen(true); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      selectOption(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIdx(-1);
    }
  }

  function handleBlur(e) {
    // Close if focus leaves both input and listbox
    if (!listRef.current?.contains(e.relatedTarget)) {
      setOpen(false);
      setActiveIdx(-1);
    }
  }

  const listboxId = "srishti-cn-listbox";

  return (
    <div className="tl-combobox-wrap">
      <input
        ref={inputRef}
        id="srishti-cn-input"
        className="tl-input"
        type="text"
        role="combobox"
        aria-label="CN code"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={activeIdx >= 0 ? `cn-opt-${activeIdx}` : undefined}
        autoComplete="off"
        placeholder="Type a code (e.g. 7208) or keyword (e.g. rebar)…"
        value={query}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />

      {open && results.length > 0 && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          className="tl-combobox-list"
          aria-label="CN code options"
        >
          {results.map((entry, idx) => (
            <li
              key={entry.code}
              id={`cn-opt-${idx}`}
              role="option"
              aria-selected={value?.code === entry.code}
              className={[
                "tl-combobox-option",
                idx === activeIdx ? "tl-combobox-option--active" : "",
                value?.code === entry.code ? "tl-combobox-option--selected" : "",
              ].join(" ").trim()}
              onMouseDown={(e) => { e.preventDefault(); selectOption(entry); }}
            >
              <span className="tl-option-code">{entry.code}</span>
              <span className="tl-option-desc">{entry.desc}</span>
            </li>
          ))}
        </ul>
      )}

      {open && results.length === 0 && query && (
        <ul ref={listRef} id={listboxId} role="listbox" className="tl-combobox-list" aria-label="CN code options">
          <li className="tl-combobox-empty" style={{ padding: "0.5rem 0.75rem", fontSize: "0.85rem" }}>
            No codes match &ldquo;{query}&rdquo;
          </li>
          {/^\d{4,8}$/.test(query.trim()) && (
            <li
              role="option"
              aria-selected={false}
              className="tl-combobox-option"
              style={{ borderTop: "1px solid rgba(28,36,32,0.1)", fontStyle: "italic" }}
              onMouseDown={(e) => { e.preventDefault(); selectOption({ code: query.trim(), desc: "Custom code — not in curated list" }); }}
            >
              <span className="tl-option-code">{query.trim()}</span>
              <span className="tl-option-desc">Use this code →</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// ── Srishti ───────────────────────────────────────────────────────────────────

export default function Srishti() {
  const navigate = useNavigate();
  const [searchParams]    = useSearchParams();
  const [phase,           setPhase]           = useState(PHASE.LOADING);
  const [gstin,           setGstin]           = useState("");
  const [sector,          setSector]          = useState("");
  const [userId,          setUserId]          = useState("");
  const [cnEntry,         setCnEntry]         = useState(null);   // { code, desc } | null
  const [startDate,       setStartDate]       = useState("");
  const [endDate,         setEndDate]         = useState("");
  const [formError,       setFormError]       = useState("");
  const [complianceScope, setComplianceScope] = useState(["CBAM"]);
  const [accessToken,     setAccessToken]     = useState("");
  const [complianceType,  setComplianceType]  = useState("CBAM");
  const [userName,        setUserName]        = useState("");
  const [userEmail,       setUserEmail]       = useState("");

  // ── Load org context on mount ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const client = getSupabaseClient();
        const { data: { session } } = await client.auth.getSession();
        if (!session) { navigate("/login", { replace: true }); return; }

        const uid = session.user.id;
        if (!cancelled) {
          setAccessToken(session.access_token || "");
          setUserEmail(session.user?.email || "");
        }

        const { data: userRow, error: userErr } = await client
          .from("users").select("gstin, full_name").eq("id", uid).single();
        if (userErr || !userRow) throw new Error("user-missing");

        const { data: org, error: orgErr } = await client
          .from("organisations").select("gstin, sector, compliance_scope").eq("gstin", userRow.gstin).single();
        if (orgErr || !org) throw new Error("org-missing");

        if (!cancelled) {
          setUserId(uid);
          setUserName(userRow.full_name || "");
          setGstin(org.gstin);
          setSector(org.sector);
          const scope = org.compliance_scope || ["CBAM"];
          setComplianceScope(scope);
          const typeParam = searchParams.get("type");
          if (typeParam && scope.includes(typeParam)) {
            setComplianceType(typeParam);
          }
          setPhase(PHASE.READY);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("srishti: load failed", err);
        if (!cancelled) setPhase(PHASE.ERROR);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [navigate, searchParams]);

  // ── Submit ────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");

    // Client-side validation
    if (!cnEntry) {
      setFormError("Please select a CN code before submitting.");
      return;
    }
    if (!startDate || !endDate) {
      setFormError("Both period start and end dates are required.");
      return;
    }
    if (endDate < startDate) {
      setFormError("End date must be on or after the start date.");
      return;
    }

    setPhase(PHASE.SUBMITTING);
    try {
      const client = getSupabaseClient();
      const { error } = await client.from("passports").insert({
        gstin,
        cn_code:                cnEntry.code,
        reporting_period_start: startDate,
        reporting_period_end:   endDate,
        status:                 "draft",
        created_by:             userId,
        compliance_type:        complianceType,
      });

      if (error) throw error;
      navigate("/dashboard", { replace: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("srishti: insert failed", err);
      setPhase(PHASE.READY);
      setFormError(PUBLIC_ERROR);
    }
  }

  const isSubmitting = phase === PHASE.SUBMITTING;

  // ── Loading ───────────────────────────────────────────────────────────────
  if (phase === PHASE.LOADING) {
    return (
      <Koshtha role="customer" accessToken={accessToken} userName={userName} userEmail={userEmail} complianceScope={complianceScope}>
        <main className="tl-page tl-page--centered">
          <p className="tl-loading">Loading…</p>
        </main>
      </Koshtha>
    );
  }

  // ── Error (load failure) ──────────────────────────────────────────────────
  if (phase === PHASE.ERROR) {
    return (
      <Koshtha role="customer" accessToken={accessToken} userName={userName} userEmail={userEmail} complianceScope={complianceScope} onSignOut={() => navigate("/dashboard")}>
        <main className="tl-page tl-page--centered">
          <p className="tl-error">{PUBLIC_ERROR}</p>
        </main>
      </Koshtha>
    );
  }

  // ── Ready / Submitting ────────────────────────────────────────────────────
  return (
    <Koshtha role="customer" accessToken={accessToken} complianceScope={complianceScope} onSignOut={() => navigate("/dashboard")}>
      <main className="tl-page tl-page--form">
        <header className="tl-form-header">
          <h1 className="tl-form-title">New {PASSPORT_LABEL}</h1>
          <p className="tl-sub">
            Select the commodity code and reporting period. Emissions data can
            be added after creation.
          </p>
        </header>

        <form className="tl-form" onSubmit={handleSubmit} noValidate>

          {/* Compliance type — only shown for multi-framework orgs */}
          {complianceScope.length > 1 && (
            <fieldset className="tl-fieldset">
              <legend className="tl-legend">Compliance framework</legend>
              <div className="tl-radio-group">
                {complianceScope
                  .filter((fw) => ["CBAM", "BRSR", "CCTS"].includes(fw))
                  .map((fw) => (
                    <label key={fw} className="tl-radio-label">
                      <input
                        type="radio"
                        name="compliance_type"
                        value={fw}
                        checked={complianceType === fw}
                        onChange={() => setComplianceType(fw)}
                        aria-label={fw}
                        disabled={isSubmitting}
                      />
                      {fw}
                    </label>
                  ))}
              </div>
            </fieldset>
          )}

          {/* CN code */}
          <fieldset className="tl-fieldset">
            <legend className="tl-legend">Commodity (CN code)</legend>
            <label className="tl-label" htmlFor="srishti-cn-input">
              CN code
            </label>
            <CnCodeCombobox
              sector={sector}
              value={cnEntry}
              onChange={setCnEntry}
            />
            {cnEntry && (
              <p
                className="tl-selected-code"
                aria-label="selected CN code"
              >
                <span className="tl-option-code">{cnEntry.code}</span>
                &ensp;{cnEntry.desc}
              </p>
            )}
          </fieldset>

          {/* Reporting period */}
          <fieldset className="tl-fieldset">
            <legend className="tl-legend">Reporting period</legend>
            <div className="tl-period-row">
              <div className="tl-period-field">
                <label className="tl-label" htmlFor="srishti-start">
                  Period start
                </label>
                <input
                  id="srishti-start"
                  className="tl-input"
                  type="date"
                  aria-label="period start"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="tl-period-field">
                <label className="tl-label" htmlFor="srishti-end">
                  Period end
                </label>
                <input
                  id="srishti-end"
                  className="tl-input"
                  type="date"
                  aria-label="period end"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </fieldset>

          {/* Errors */}
          {formError && (
            <p className="tl-error" role="alert">{formError}</p>
          )}

          {/* Submit */}
          <button
            className="tl-button tl-button--form"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating…" : `Create ${PASSPORT_LABEL}`}
          </button>

        </form>
      </main>
    </Koshtha>
  );
}
